/* global PublicKeyCredential */

import { GetWebAuthChallenge, UpdateWebAuthCredential, UpdateBackendSession, VerifyLogin } from "../service/SpatApi";
import { getDataFromIndexDB } from "../utils/dataStore";
import { signInWithCustomTokenFirebase } from "./fireBaseToken";
import { requestForToken } from '../messaging/firebaseMessaging';
import { showErrorToast } from "./Toasts";

// ✅ Convert base64url → Uint8Array
const base64urlToUint8Array = (base64url) => {
    const padding = '='.repeat((4 - (base64url.length % 4)) % 4);
    const base64 = base64url.replace(/-/g, '+').replace(/_/g, '/') + padding;
    return Uint8Array.from(atob(base64), c => c.charCodeAt(0));
};

// ✅ Convert ArrayBuffer → base64url string
const arrayBufferToBase64url = (buffer) => {
    const binary = String.fromCharCode(...new Uint8Array(buffer));
    return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
};


// --- Main Biometric Utility Functions ---

export const registerBiometricCredential = async () => {

    try {
        const email = await getDataFromIndexDB("email");
        console.log("Initiating biometric credential registration...");
        const response = await GetWebAuthChallenge(email);
        // Add a check for response.data before destructuring
        if (!response || !response.data) {
            throw new Error("API response for WebAuthn challenge is missing data.");
        }
        const { challenge, userId, displayName } = response.data;

        // Check if challenge and userId are present
        if (!challenge || !userId) {
            throw new Error("Server did not provide a valid challenge or userId for registration.");
        }

        // WebAuthn registration options
        const publicKeyOptions = {
            challenge: base64urlToUint8Array(challenge),
            rp: { name: "SriPonnachi", id: window.location.hostname }, // Use hostname for RP ID for security
            user: {
                id: new TextEncoder().encode(userId), // User ID must be a Uint8Array
                name: email,
                displayName: displayName || "User Account", // More descriptive default
            },
            pubKeyCredParams: [
                { type: 'public-key', alg: -7 },  // ES256
                { type: 'public-key', alg: -257 } // RS256
            ],
            authenticatorSelection: {
                userVerification: "preferred", // Preferred means authenticator decides, but will try
                authenticatorAttachment: "platform", // Prefer built-in authenticators (Face ID, Touch ID)
                requireResidentKey: false, // Don't require discoverable credentials unless specifically needed
            },
            timeout: 60000, // 60 seconds timeout
        };

        // Start WebAuthn registration process
        const credential = await navigator.credentials.create({ publicKey: publicKeyOptions });

        // Check if credential and response exist
        if (!credential || !credential.response) {
            throw new Error("Biometric credential creation failed or response is missing.");
        }

        // Convert ArrayBuffer rawId to base64url string for server storage
        let credentialPayload = {
            id: credential.id,
            rawId: arrayBufferToBase64url(credential.rawId),
            type: credential.type,
            response: {
                attestationObject: arrayBufferToBase64url(credential.response.attestationObject),
                clientDataJSON: arrayBufferToBase64url(credential.response.clientDataJSON)
            }
        };
        const credentialId = arrayBufferToBase64url(credential.rawId);
        credentialPayload = {
            ...credentialPayload,
            credentialId,
            challenge
        }

        const result = await UpdateWebAuthCredential(credentialPayload);

        if (result.status !== 200) {
            throw new Error(`Failed to store credentialId on server. Status: ${result.status}`);
        }

        console.log("✅ Biometric credential registered successfully and stored on server.");
        return true;
    } catch (error) {
        console.error("❌ Biometric registration failed:", error);
        // Provide user-friendly error messages based on common WebAuthn errors
        if (error.name === 'NotAllowedError' || error.name === 'SecurityError') {
            throw new Error("Biometric registration was denied or failed due to security settings. Please ensure biometrics are enabled and permitted for this site.");
        } else if (error.name === 'AbortError') {
            throw new Error("Biometric registration was cancelled by the user.");
        } else if (error.name === 'ConstraintError') {
            throw new Error("A credential already exists for this user or device.");
        }
        else {
            throw new Error(error);
        }
    }
};

// 🔐 Prompt biometric auth (for login/unlocking)
export const promptBiometricAuth = async () => {
    try {
        const isBiometricAvailable = await isWebAuthnAvailable();
        if (!isBiometricAvailable) {
            return {
                success: false,
                isBiometricPossible: false,
                message: "Biometric authentication is not available on this device.",
            };
        }
        const email = await getDataFromIndexDB("email");
        console.log('Prompting biometric authentication...');
        const response = await GetWebAuthChallenge(email);
        // Add a check for response.data before destructuring
        if (!response || !response.data) {
            throw new Error("API response for WebAuthn challenge is missing data.");
        }
        const { challenge, credentialId } = response.data;

        if (!challenge || !credentialId) {
            console.error("Server did not provide a valid challenge or credentialId for authentication.");
            return { success: false, isBiometricPossible: false };
        }

        const publicKeyOptions = {
            challenge: base64urlToUint8Array(challenge),
            allowCredentials: [{
                id: base64urlToUint8Array(credentialId),
                type: "public-key",
            }],
            userVerification: "preferred", // Preferred means authenticator decides
            timeout: 60000,
        };

        // This call will trigger the biometric prompt (e.g., fingerprint, face scan)
        const assertion = await navigator.credentials.get({ publicKey: publicKeyOptions });

        // Ensure the returned object is a PublicKeyCredential
        if (!(assertion instanceof PublicKeyCredential)) {
            throw new Error("Invalid assertion returned from authenticator during biometric authentication.");
        }

        // Prepare payload to send to backend
        const payload = {
            credentialId: assertion.id,
            clientDataJSON: arrayBufferToBase64url(assertion.response.clientDataJSON),
            authenticatorData: arrayBufferToBase64url(assertion.response.authenticatorData),
            signature: arrayBufferToBase64url(assertion.response.signature),
            userHandle: assertion.response.userHandle
                ? arrayBufferToBase64url(assertion.response.userHandle)
                : null
        };
        const responseUpdateBackendSession = await UpdateBackendSession(payload);
        if (responseUpdateBackendSession.status !== 'Authentication successful and session extended') {
            console.error("❌ Verification failed:", responseUpdateBackendSession?.error);
            throw new Error(responseUpdateBackendSession?.error || "Authentication verification failed");
        }

        const newSingedToken = await signInWithCustomTokenFirebase(responseUpdateBackendSession.newToken);
        const deviceNotificationRegistration = await requestForToken();

        const reponseVerifyLogin = await VerifyLogin(newSingedToken, JSON.stringify({ newLoginIn: false, deviceNotificationRegistration }));
        if (!reponseVerifyLogin || reponseVerifyLogin === false) {
            throw new Error("Token Reauth Failed");
        }
        console.log("✅ Biometric authentication verified on server.");
        return { success: true, user: reponseVerifyLogin?.user };
    } catch (error) {
        console.error("🔐 Biometric authentication failed:", error);
        // Provide user-friendly error messages
        if (error.name === 'NotAllowedError' || error.name === 'SecurityError') {
            throw new Error("Biometric authentication was denied or failed due to security settings. Please ensure biometrics are enabled and permitted for this site.");
        } else if (error.name === 'AbortError') {
            throw new Error("Biometric authentication was cancelled by the user.");
        } else if (error.name === 'NotFoundError') {
            throw new Error("No registered biometric credential found for this user. Please register biometrics first.");
        }
        else {
            throw new Error(error);
        }
    }
};

export async function detectFeatures(getLocation) {
    const ua = navigator.userAgent || navigator.vendor || window.opera;

    // 🌐 Feature support checks
    const isStandalone =
        window.matchMedia('(display-mode: standalone)').matches ||
        window.navigator.standalone === true;

    // 📱 Device type and platform detection
    const isAndroid = /android/i.test(ua);
    const isIOS = /iphone|ipad|ipod/i.test(ua);
    //const isSamsung = /SamsungBrowser/i.test(ua);
    const isMobile = /Mobi|Android/i.test(ua);
    const isTablet = /Tablet|iPad/i.test(ua);
    const isDesktop = !isMobile && !isTablet;

    const fromGooglePlay = /google play/i.test(document.referrer || '');

    let platform = 'unknown';
    if (isIOS) platform = 'iOS';
    else if (isAndroid) platform = 'Android';
    else if (isDesktop) platform = 'Desktop';

    let source = 'browser';
    if (isStandalone || fromGooglePlay) {
        source = 'app';
    }

    // --- 📍 Location Detection (Prioritizing Geolocation API) ---
    let location = 'unknown';
    let locationSource = 'unknown'; // Tracks how location was obtained
    if (getLocation) {
        // 1. Attempt to use the Geolocation API (most accurate, requires user consent)
        if ("geolocation" in navigator) {
            try {
                const position = await new Promise((resolve, reject) => {
                    navigator.geolocation.getCurrentPosition(
                        resolve,
                        (error) => {
                            console.warn('Geolocation API error:', error.message, `(Code: ${error.code})`);
                            reject(error); // Reject the promise on any Geolocation API error
                        },
                        {
                            enableHighAccuracy: true, // Request the most accurate position
                            timeout: 7000,           // Max time (ms) to wait for a position
                            maximumAge: 0            // Force a fresh position, no cached data
                        }
                    );
                });

                const { latitude, longitude } = position.coords;
                location = `Latitude: ${latitude}, Longitude: ${longitude}`;
                locationSource = 'geolocation_api';
                try {
                    const geoRes = await fetch(`https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${latitude}&lon=${longitude}`);
                    const geoData = await geoRes.json();

                    if (geoData && geoData.address) {
                        const addr = geoData.address;
                        location = `${addr.city || addr.town || addr.village || ''}, ${addr.state || addr.region || ''}, ${addr.country || ''}`
                            .replace(/^,|,$/g, '') // Remove leading/trailing commas
                            .replace(/, ,/g, ', '); // Fix double commas
                        locationSource = 'nominatim_reverse_geocoded';
                    } else {
                        location = `Latitude: ${latitude}, Longitude: ${longitude}`;
                    }
                } catch (e) {
                    console.warn('Nominatim reverse geocoding failed:', e);
                    location = `Latitude: ${latitude}, Longitude: ${longitude}`;
                    locationSource = 'unknown';
                }


            } catch (error) {
                // Handle specific Geolocation API errors to set appropriate locationSource
                switch (error.code) {
                    case error.PERMISSION_DENIED:
                        location = "User denied location access";
                        locationSource = 'geolocation_denied';
                        break;
                    case error.POSITION_UNAVAILABLE:
                        location = "Geolocation position unavailable";
                        locationSource = 'geolocation_unavailable';
                        break;
                    case error.TIMEOUT:
                        location = "Geolocation request timed out";
                        locationSource = 'geolocation_timeout';
                        break;
                    default:
                        location = "Geolocation error";
                        locationSource = 'geolocation_error';
                }
                console.log("Falling back to IP-based location due to Geolocation failure or denial...");
            }
        } else {
            console.log("Geolocation is not supported by this browser. Falling back to IP-based location...");
        }

        // 2. Fallback to IP-based location if Geolocation failed, was denied, or not supported
        // This block runs only if geolocation didn't successfully provide a location or encountered a specific error.
        if (locationSource === 'unknown' || locationSource.startsWith('geolocation_denied') ||
            locationSource.startsWith('geolocation_unavailable') || locationSource.startsWith('geolocation_timeout') ||
            locationSource.startsWith('geolocation_error')) {
            try {
                const res = await fetch('https://ipwho.is/');
                const data = await res.json();

                if (data.success) {
                    location = `${data.city}, ${data.region}, ${data.country}`;
                    locationSource = 'ipwhois';
                } else {
                    // fallback to ipapi.co
                    const res2 = await fetch('https://ipapi.co/json/');
                    const data2 = await res2.json();
                    if (data2.city || data2.region || data2.country_name) {
                        location = `${data2.city || ''}, ${data2.region || ''}, ${data2.country_name || ''}`;
                        locationSource = 'ipapi_co';
                    }
                }
            } catch (e) {
                console.warn('IP location fetch failed:', e);
                location = "IP-based location fetch failed.";
                locationSource = 'ip_fetch_failed';
            }
        }

    }

    return {
        platform,
        source,
        location
    };
}

export const isWebAuthnAvailable = async () => {
    const isWebAuthnSupported = !!window.PublicKeyCredential;

    if (
        isWebAuthnSupported &&
        typeof PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable === "function"
    ) {
        try {
            const isBiometricAvailable = await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();

            if (!isBiometricAvailable) {
                showErrorToast("பயோமெட்ரிக் உள்நுழைவை பயன்படுத்த, தயவுசெய்து உங்கள் சாதனத்தில் PIN அல்லது பயோமெட்ரிக்ஸ் அமைக்கவும்.");
            }

            return isBiometricAvailable;
        } catch (e) {
            console.warn("Biometric platform authenticator check failed:", e);
        }
    }

    return false;
};
